import React, { useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Download, Plus, Search } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import { useMutation, useQueryClient } from "react-query";

import useUsers from "hooks/useUsers";
import useUserStats from "hooks/useUserStats";
import { User, UserCreate } from "types/user.type";
import { archiveUser, createAgent, exportUsers } from "services/user.service";
import Modal from "Common/Components/Modal";
import DeleteModal from "Common/DeleteModal";
import { Pager } from "Common/Table";
import AgentForm from "./AgentForm";
import UsersTable from "./UsersTable";
import { USER_SEGMENTS, segmentPath } from "./segments";

const ListView = () => {
  const [showForm, setShowForm] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const { data, isLoading, onQueryChange, query } = useUsers();

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: userStats } = useUserStats();

  const { isLoading: creating, mutate } = useMutation(
    async (input: UserCreate) => await createAgent(input),
    {
      onSuccess(data) {
        toast.success(data?.message);
        queryClient.invalidateQueries();
        toggleAdd();
      },
      onError(error: any) {
        toast.error(error?.response?.data?.message);
      },
    }
  );

  const { isLoading: archiving, mutate: archive } = useMutation(
    async (input: string) => await archiveUser(input),
    {
      onSuccess(data) {
        toast.success(data?.message);
        queryClient.invalidateQueries();
        toggleConfirm();
        navigate("/archived-users");
      },
      onError(error: any) {
        toast.error(error?.response?.data?.message);
      },
    }
  );

  const { isLoading: exporting, mutate: exportMutation } = useMutation(
    async (input: { role: string; kycStatus: string }) =>
      await exportUsers(input.role, input.kycStatus),
    {
      onSuccess() {
        toast.success("Export ready");
      },
      onError(error: any) {
        toast.error(error?.response?.data?.message);
      },
    }
  );

  const toggleAdd = () => setShowForm((value) => !value);
  const toggleConfirm = () => setShowConfirm((value) => !value);

  const handleArchive = (event: React.MouseEvent, target: User) => {
    // The row itself opens the profile, so without this an Archive tap opens
    // the person as well as the confirm dialog.
    event.stopPropagation();
    setUser(target);
    toggleConfirm();
  };

  const total = data?.data?.total ?? 0;
  const page = Number(data?.data?.page ?? 1);
  const totalPages = Math.max(1, Math.ceil(total / (query.limit ?? 20)));

  return (
    <React.Fragment>
      <BreadCrumb title="Registered users" pageTitle="Mynda" />
      <ToastContainer closeButton={false} limit={1} />

      <div className="flex justify-end mb-4">
        <button
          onClick={toggleAdd}
          type="button"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
        >
          <Plus className="inline-block size-4" />
          Create Agent
        </button>
      </div>

      {/*
        Each tile opens its own page rather than filtering this one. Filtering
        in place left the URL unchanged, so a group could not be linked to,
        reloaded or backed out of, and this heading went on saying "Registered
        users" whatever the table had become.

        Deliberately compact: nine tiles at the old size were about 810px of
        chrome to scroll past on a phone before reaching a single row.
      */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-5">
        {USER_SEGMENTS.map((segment) => (
          <Link
            key={segment.key}
            to={segmentPath(segment.key)}
            className="group flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors"
          >
            <span className="min-w-0">
              <span className="block text-base font-semibold leading-tight text-gray-900">
                {segment.count(userStats?.data)}
              </span>
              <span className="block text-[11px] leading-tight text-gray-500">
                {segment.label}
              </span>
            </span>
            <ChevronRight
              size={14}
              className="shrink-0 text-gray-300 group-hover:text-indigo-500"
            />
          </Link>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" id="usersTable">
        <div className="px-4 py-3 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative grow sm:max-w-xs">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              onChange={({ target }) => onQueryChange("search", target.value)}
              placeholder="Search name, email, role or status"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md"
            />
          </div>
          <button
            onClick={() => exportMutation({ role: "", kycStatus: "" })}
            disabled={exporting}
            type="button"
            className="inline-flex self-start items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-60 transition-colors sm:ml-auto"
          >
            <Download className="inline-block size-4" />
            {exporting ? "Exporting…" : "Export"}
          </button>
        </div>

        <UsersTable
          users={data?.data?.data}
          total={total}
          isLoading={isLoading}
          onView={(target) => navigate(`/pages-account/${target._id}`)}
          onArchive={handleArchive}
        />

        <Pager
          page={page}
          totalPages={totalPages}
          total={total}
          onPage={(next) => onQueryChange("page", next)}
        />
      </div>

      <Modal show={showForm} onHide={toggleAdd} id="createAgentModal" size="md">
        <Modal.Header
          className="flex items-center justify-between p-4 border-b border-gray-200"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500"
        >
          <Modal.Title className="text-base font-semibold text-gray-900">
            Create Agent
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <AgentForm
            onSubmit={(payload: UserCreate) => mutate(payload)}
            isLoading={creating}
            onCancel={toggleAdd}
          />
        </Modal.Body>
      </Modal>

      <DeleteModal
        show={showConfirm}
        onHide={toggleConfirm}
        onDelete={() => {
          if (user) archive(user._id);
        }}
        title="Archive this user?"
        message={
          user
            ? `${user.firstName} ${user.lastName} will move to Archived users.`
            : ""
        }
        actionText={archiving ? "Archiving…" : "Archive"}
      />
    </React.Fragment>
  );
};

export default ListView;
